<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, reactive, ref, watch } from "vue"
import { useMachine } from "@xstate/vue"
import { createFormPdlMachine } from "@core/components/source/meter/form/pdl/machine/pdl-form.machine"
import type { PdlFormSearchExistPayload } from "@core/components/source/meter/form/pdl/PdlFormSearch.vue"
import type { SourceAlreadyExistDonePayload } from "@core/components/source/meter/form/invitation/error/SourceAlreadyExist.vue"
import type { OwnerOutput } from "@core/composables"
import type { Address } from "@types"
import type { SourceAppendDonePayload } from "@core/components/source/form/append/source-append.types"
// ================================
// TYPES & INTERFACES
// ================================
export interface PdlFormProps {
    buildingId?: string
    /** Adresse du bâtiment pour la recherche PDL */
    address?: Address | null
    /**
     * Affiche le Retour sur l’étape titulaire (modale d’ajout de source)
     */
    showOwnerStepBack?: boolean
    /**
     * true si la modale a été ouverte avec un type présélectionné (pas de sélecteur) : Retour titulaire / shell masqués
     */
    appendSkippedTypeSelector?: boolean
    /**
     * Si true : PUT sign avec `disable_task: true` (ex. onboarding bâtiment où la tâche est lancée manuellement après).
     * @default false
     */
    consentSignDisableTask?: boolean
}

export type PdlFormShellBackPayload = {
    /** Masque le lien Retour du parent (ex. dialog append) */
    hidden: boolean
    /** Désactive le lien Retour (ex. pendant la signature API) */
    disabled: boolean
}

interface Emits {
    (e: "back"): void
    /** Fermeture du parcours après succès ; `_id_source` si créé (signature consentement) */
    (e: "done", _id_source?: string, source?: SourceAppendDonePayload): void
    (e: "update:shellBack", payload: PdlFormShellBackPayload): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<PdlFormProps>(), {
    buildingId: undefined,
    address: undefined,
    showOwnerStepBack: false,
    appendSkippedTypeSelector: false,
    consentSignDisableTask: false,
})
const emit = defineEmits<Emits>()

// ================================
// MACHINE
// ================================
const { snapshot, send } = useMachine(createFormPdlMachine())

const stateName = computed(() => {
    const v = snapshot.value.value
    return typeof v === "string" ? v : ""
})

const isStorybook = import.meta.env.STORYBOOK === "true"
const machineStateForStorybook = isStorybook
    ? reactive<{ value: unknown; context: unknown }>({
          value: undefined,
          context: undefined,
      })
    : undefined

if (isStorybook && machineStateForStorybook) {
    watch(
        snapshot,
        (s) => {
            machineStateForStorybook.value = s.value
            machineStateForStorybook.context = s.context
        },
        { immediate: true }
    )
}

defineExpose(
    isStorybook && machineStateForStorybook
        ? { machineState: machineStateForStorybook }
        : {}
)

/** Au retour sur SourceFormOwner (ex. BACK depuis PdlFormSearch), restaure le titulaire depuis le contexte pour réafficher "Mon entreprise" si besoin */
watch(stateName, (name, prevName) => {
    if (
        name === "SourceFormOwner" &&
        prevName !== "SourceFormOwner" &&
        snapshot.value.context.owner
    ) {
        ownerOutput.value = snapshot.value.context.owner
    }
})

// ================================
// INTERACTIONS
// ================================
function onBack(): void {
    send({ type: "BACK" })
    if (stateName.value === "idle") emit("back")
}

function onDone(): void {
    const _id_source = snapshot.value.context._id_source
    if (!_id_source) {
        emit("done", _id_source)
        return
    }
    const source: SourceAppendDonePayload = {
        _id_source,
        idsource: snapshot.value.context.pdl,
        type: "pdl",
        idtype: "pdl",
        operator: "enedis",
        isInvitationSent: false,
    }
    if (snapshot.value.context.taskOnboarding != null) {
        source.taskOnboarding = snapshot.value.context.taskOnboarding
    }
    emit("done", _id_source, source)
}

const ownerOutput = ref<OwnerOutput | null>(null)

/** Signature consentement en cours (API) — pour désactiver le Retour du shell */
const isConsentSigning = ref(false)

function emitShellBackState(): void {
    const n = stateName.value
    emit("update:shellBack", {
        hidden: n === "done" || props.appendSkippedTypeSelector,
        disabled: n === "SourceConsentSign" && isConsentSigning.value,
    })
}

watch(stateName, emitShellBackState, { immediate: true })
watch(isConsentSigning, emitShellBackState)
watch(() => props.appendSkippedTypeSelector, emitShellBackState)

function onOwnerSubmit(): void {
    const owner = ownerOutput.value
    if (!owner) return
    send({ type: "OWNER_SUBMIT", owner })
}

/** Retour modale → sélecteur de type (sans toucher à la machine PDL tant qu’on quitte le composant) */
function onOwnerStepBack(): void {
    emit("back")
}

function onExist(payload: PdlFormSearchExistPayload): void {
    if (payload.exists) {
        send({
            type: "PDL_EXIST",
            invitation: payload.invitation,
            consentementUser: payload.consentementUser,
            pdl: payload.pdl,
            _id_source: payload._id_source,
            sameTitulaireEmail: payload.sameTitulaireEmail,
        })
    } else {
        send({
            type: "PDL_NOT_EXIST",
            invitation: payload.invitation,
            pdl: payload.pdl,
        })
    }
}

function onConsentSigned(payload: {
    _id_source?: string
    sourceId?: string
    contractName?: string
    taskOnboarding?: unknown
}): void {
    const _id_source = payload._id_source ?? payload.sourceId
    if (!_id_source) return
    send({
        type: "CONSENT_SIGNED",
        _id_source,
        contractName: payload.contractName,
        taskOnboarding: payload.taskOnboarding,
    })
}

function onInvitationSent(payload: {
    _id_consentrequest: string
    url: string
    _id_source?: string
}): void {
    send({
        type: "INVITATION_SENT",
        _id_consentrequest: payload._id_consentrequest,
        url: payload.url,
        _id_source: payload._id_source,
    })
}

/** Payload renvoyé par SourceInvitationShare après création de la demande de partage */
function onAskSharingSent(payload: {
    _id?: string
    token?: string
    url?: string
}): void {
    const id = payload._id ?? ""
    const path = `/demande/partage-consentement/${id}`
    const link =
        payload.url?.trim() ||
        (typeof window !== "undefined"
            ? `${window.location.origin}${path}`
            : path)
    send({
        type: "ASK_SHARING_SENT",
        _id_consentrequest: id,
        url: link,
    })
}

/** Fermeture du formulaire depuis SourceInvitationSignSent (pas de retour en arrière). */
function onInvitationSentDone(): void {
    send({ type: "UPDATE_FORM_DONE" })
    const _id_source = snapshot.value.context._id_source
    if (!_id_source) {
        emit("done", _id_source)
        return
    }
    emit("done", _id_source, {
        _id_source,
        idsource: snapshot.value.context.pdl,
        type: "pdl",
        idtype: "pdl",
        operator: "enedis",
        consentrequest: {
            _id: snapshot.value.context._id_consentrequest,
            state: "pending",
        },
        isInvitationSent: true,
    })
}

function onAlreadyExistDone(payload?: SourceAlreadyExistDonePayload): void {
    send({ type: "UPDATE_FORM_DONE" })
    emit("done", payload?._id_source)
}

function onExistErrorRetry(): void {
    send({ type: "RETRY" })
}

/** Fermeture du formulaire depuis SourceInvitationShareSent (bouton Terminer, comme SourceInvitationSignSent). */
function onAskSharingSentDone(): void {
    send({ type: "UPDATE_FORM_DONE" })
    const _id_source = snapshot.value.context._id_source
    if (!_id_source) {
        emit("done", _id_source)
        return
    }
    emit("done", _id_source, {
        _id_source,
        idsource: snapshot.value.context.pdl,
        type: "pdl",
        idtype: "pdl",
        operator: "enedis",
        consentrequest: {
            _id: snapshot.value.context._id_consentrequest,
            state: "pending",
        },
        isInvitationSent: true,
    })
}

/** Props pour SourceConsentSign : construit à partir du contexte machine et des props (PDL non existant → signature) */
const consentSignOwner = computed(() => snapshot.value.context.owner)
const consentSignSource = computed(() => ({
    idsource: snapshot.value.context.pdl ?? "",
    idtype: "pdl" as const,
}))
const consentSignBuilding = computed(() => ({
    _id_building: props.buildingId ?? "",
    address: props.address ?? {
        street: "",
        city: "",
        zipcode: "",
    },
}))
const consentSignRequest = computed(() => ({
    type: "SIGN" as const,
    mail: true,
    duration: 5 * 12,
    disable_task: props.consentSignDisableTask,
}))

onMounted(() => {
    send({ type: "START" })
})
</script>

<template>
    <div class="source-append-pdl flex flex-col gap-6 w-full">
        <!-- Titulaire (étape optionnelle invitation) -->
        <template v-if="stateName === 'SourceFormOwner'">
            <SourceFormOwner v-model="ownerOutput">
                <template #footer>
                    <div
                        class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
                    >
                        <BaseButton
                            v-if="
                                showOwnerStepBack && !appendSkippedTypeSelector
                            "
                            label="Retour"
                            severity="secondary"
                            class="min-w-[10rem]"
                            @click="onOwnerStepBack"
                        />
                        <BaseButton
                            label="Suivant"
                            class="min-w-[10rem]"
                            :disabled="!ownerOutput"
                            @click="onOwnerSubmit"
                        />
                    </div>
                </template>
            </SourceFormOwner>
        </template>
        <!-- Recherche PDL -->
        <PdlFormSearch
            v-else-if="stateName === 'PdlFormSearch'"
            :address="address ?? undefined"
            :owner="snapshot.context.owner"
            :invitation="snapshot.context.invitation"
            @back="onBack"
            @exist="onExist"
        />
        <!-- Signature consentement -->
        <SourceConsentSign
            v-else-if="
                stateName === 'SourceConsentSign' &&
                consentSignOwner &&
                consentSignSource.idsource
            "
            :owner="consentSignOwner"
            :source="consentSignSource"
            :building="consentSignBuilding"
            :request="consentSignRequest"
            @back="onBack"
            @signed="onConsentSigned"
            @update:signing="isConsentSigning = $event"
        />
        <!-- Invitation -->
        <SourceInvitationSign
            v-else-if="stateName === 'SourceInvitationSign'"
            :building-id="props.buildingId ?? ''"
            :address="props.address ?? {}"
            :contract-name="snapshot.context.contractName ?? ''"
            :idsource="snapshot.context.pdl ?? ''"
            :owner="
                snapshot.context.owner ?? {
                    user: { firstname: '', lastname: '', username: '' },
                    company: null,
                }
            "
            type="pdl"
            @back="onBack"
            @sent="onInvitationSent"
        />
        <SourceInvitationSignSent
            v-else-if="stateName === 'SourceInvitationSignSent'"
            :consent-request-id="snapshot.context._id_consentrequest ?? ''"
            :link="snapshot.context.url ?? ''"
            consent-request-type="SIGN"
            :multiple="false"
            :send-back="false"
            @done="onInvitationSentDone"
        />
        <!-- Demande de partage -->
        <SourceInvitationShare
            v-else-if="stateName === 'SourceInvitationShare'"
            :source-id="snapshot.context._id_source ?? ''"
            :idsource="snapshot.context.pdl ?? ''"
            :building-id="buildingId ?? ''"
            :is-pce="false"
            @back="onBack"
            @sent="onAskSharingSent"
        />
        <SourceInvitationShareSent
            v-else-if="stateName === 'SourceInvitationShareSent'"
            :consent-request-id="snapshot.context._id_consentrequest ?? ''"
            :link="snapshot.context.url ?? ''"
            consent-request-type="SHARE"
            :multiple="false"
            :send-back="false"
            @done="onAskSharingSentDone"
        />
        <!-- Déjà existant -->
        <SourceAlreadyExist
            v-else-if="stateName === 'SourceAlreadyExist'"
            :source-id="snapshot.context._id_source ?? ''"
            :idsource="snapshot.context.pdl ?? ''"
            :building-id="buildingId ?? ''"
            :is-pce="false"
            :from-building="false"
            @done="onAlreadyExistDone"
            @click:quit="onBack"
        />
        <!-- Erreur -->
        <SourceExistError
            v-else-if="stateName === 'SourceExistError'"
            :idsource="snapshot.context.pdl ?? ''"
            :is-pce="false"
            @retry="onExistErrorRetry"
            @click:quit="onBack"
        />
        <!-- Bouton Retour si idle (ne devrait pas s'afficher en flux normal) -->
        <div
            v-else-if="stateName === 'idle' && !appendSkippedTypeSelector"
            class="flex gap-3"
        >
            <BaseButton label="Retour" severity="secondary" @click="onBack" />
        </div>
        <!-- État done (consentement signé) : message de succès + terminer -->
        <div v-else-if="stateName === 'done'" class="flex flex-col gap-4">
            <SourceConsentSignSuccessMessage
                request-type="SIGN"
                meter-type="pdl"
                :idsource="snapshot.context.pdl ?? ''"
                :address="address ?? undefined"
            />
            <BaseButton
                label="Terminer"
                @click="onDone"
                class="min-w-[10rem] w-fit self-center"
            />
        </div>
    </div>
</template>
